import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CalAngularService, ICvxClaimsPrincipal } from '@cvx-dev/cal-angular';

@Injectable({ providedIn: 'root' })
export class CalSessionService {
  private readonly calService = inject(CalAngularService);
  private readonly destroyRef = inject(DestroyRef);
  private refreshInFlight: Promise<ICvxClaimsPrincipal | null> | null = null;

  private readonly authenticatedState = signal(false);
  private readonly principalState = signal<ICvxClaimsPrincipal | null>(null);

  readonly isAuthenticated = computed(() => this.authenticatedState());
  readonly principal = computed(() => this.principalState());

  constructor() {
    this.watchAuthentication();
  }

  async refreshPrincipal(): Promise<ICvxClaimsPrincipal | null> {
    if (!this.refreshInFlight) {
      this.refreshInFlight = this.loadPrincipal().finally(() => {
        this.refreshInFlight = null;
      });
    }

    return this.refreshInFlight;
  }

  async getUserEmail(): Promise<string> {
    const currentPrincipal = this.principal();
    if (currentPrincipal?.email || currentPrincipal?.userName) {
      return currentPrincipal.email || currentPrincipal.userName || '';
    }

    const refreshedPrincipal = await this.refreshPrincipal();
    return refreshedPrincipal?.email || refreshedPrincipal?.userName || '';
  }

  private watchAuthentication(): void {
    this.calService
      .isUserSignedIn()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isSignedIn) => {
        this.authenticatedState.set(Boolean(isSignedIn));

        if (!isSignedIn) {
          this.clearSession();
          return;
        }

        this.principalState.set(this.calService.cvxClaimsPrincipal ?? null);
        void this.refreshPrincipal();
      });
  }

  private async loadPrincipal(): Promise<ICvxClaimsPrincipal | null> {
    try {
      const claims = await this.calService.getClaims();
      const principal = (claims as ICvxClaimsPrincipal | null) ?? this.calService.cvxClaimsPrincipal ?? null;

      this.principalState.set(principal);
      this.authenticatedState.set(Boolean(principal));

      return principal;
    } catch {
      const principal = this.calService.cvxClaimsPrincipal ?? null;

      this.principalState.set(principal);
      this.authenticatedState.set(Boolean(principal));

      return principal;
    }
  }

  private clearSession(): void {
    this.authenticatedState.set(false);
    this.principalState.set(null);
  }
}